import { BorderBox } from "@/components/atoms/BorderBox";
import { QuestionConditionalLogicChangedProps } from ".";
import { useSurveyCheckResultContext } from "@/features/survey_sync/context/surveyCheckResultContext";
import { Where, WhereField } from "@/types/crm/configuration/question_conditionals";
import { ListTodo } from "lucide-react";

export function QuestionConditionalLogicChanged({
    instanceConditionals,
    templateConditionals
}: QuestionConditionalLogicChangedProps) {
    const surveyCheckResult = useSurveyCheckResultContext();

    const findQuestion = (questionId: string) => {
        const instanceQuestion = surveyCheckResult.allInstanceQuestions.find(q => q.id === questionId);
        if (instanceQuestion) {
            return instanceQuestion.question || questionId;
        }

        const templateQuestion = surveyCheckResult.allTemplateQuestions.find(q => q.id === questionId);
        if (templateQuestion) {
            return templateQuestion.question || questionId;
        }

        return questionId;
    };

    const extractConditions = (where: Where | undefined, depth = 0): JSX.Element[] => {
        if (!where) return [];

        const elements: JSX.Element[] = [];

        for (const [key, value] of Object.entries(where)) {
            // Handle 'and' arrays
            if (key === 'and' && Array.isArray(value)) {
                elements.push(
                    <div key={`and-${depth}`} className="flex flex-col gap-1">
                        {/* <span className="text-xs font-semibold uppercase text-muted-foreground">AND</span> */}
                        {value.flatMap((item, idx) => extractConditions(item, depth + idx + 1))}
                    </div>
                );
            }
            else if (key === 'or' && Array.isArray(value)) {
                elements.push(
                    <div key={`or-${depth}`} className="flex flex-col gap-1">
                        {/* <span className="text-xs font-semibold uppercase text-muted-foreground">OR</span> */}
                        {value.flatMap((item, idx) => extractConditions(item, depth + idx + 1))}
                    </div>
                );
            }
            else if (!Array.isArray(value) && typeof value === 'object' && value !== null) {
                const whereField = value as WhereField;
                const questionText = findQuestion(key);

                for (const [operator, operatorValue] of Object.entries(whereField)) {
                    const displayValue = Array.isArray(operatorValue)
                        ? operatorValue.join(', ')
                        : String(operatorValue);

                    elements.push(
                        <div key={`${key}-${operator}-${depth}`} className="flex flex-col gap-1 text-sm">
                            <div className="font-medium text-xs">{questionText}</div>
                            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                                <span className="rounded bg-muted px-2 py-0.5 font-mono">{operator}</span>
                                <span className="rounded bg-muted px-2 py-0.5">{displayValue}</span>
                            </div>
                        </div>
                    );
                }
            }
        }

        return elements;
    };

    const instanceElements = extractConditions(instanceConditionals?.where);
    const templateElements = extractConditions(templateConditionals?.where);

    return (
        <BorderBox size="sm" className="flex flex-col items-start gap-3">
            <h4 className="font-semibold flex gap-1 items-center"><ListTodo size="16"/>Conditional logic will change</h4>

            <BorderBox size="xs" className="bg-white">
                <div className="grid w-full grid-cols-2 gap-4">
                    <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">From instance</span>
                        {instanceConditionals?.enabled ? (
                            <div className="flex flex-col gap-2">
                                {instanceElements.length > 0 ? (
                                    instanceElements
                                ) : (
                                    <span className="text-muted-foreground">No conditions</span>
                                )}
                            </div>
                        ) : (
                            <span className="text-muted-foreground">Disabled</span>
                        )}
                    </div>

                    <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">To template</span>
                        {templateConditionals?.enabled ? (
                            <div className="flex flex-col gap-2">
                                {templateElements.length > 0 ? (
                                    templateElements
                                ) : (
                                    <span className="text-muted-foreground">No conditions</span>
                                )}
                            </div>
                        ) : (
                            <span className="text-muted-foreground">Disabled</span>
                        )}
                    </div>
                </div>
            </BorderBox>

        </BorderBox>
    );
}